<template>
  <div class="lecture-calendar">
    <div class="flex justify-between items-center mb-4">
      <div class="flex items-center space-x-2">
        <button 
          @click="previousWeek" 
          class="p-2 rounded-full hover:bg-blue-50 text-blue-900"
        >
          <span class="material-symbols-outlined">chevron_left</span>
        </button>
        <div class="text-sm font-medium">
          {{ formatDateRange(currentWeekStart, getEndOfWeek(currentWeekStart)) }}
        </div>
        <button 
          @click="nextWeek" 
          class="p-2 rounded-full hover:bg-blue-50 text-blue-900"
        >
          <span class="material-symbols-outlined">chevron_right</span>
        </button>
        <button 
          @click="goToCurrentWeek" 
          class="px-3 py-1 rounded text-sm bg-blue-100 text-blue-900 hover:bg-blue-200"
        >
          Today
        </button>
      </div>
    </div>
    
    <div class="overflow-x-auto">
      <div class="min-w-[768px]">
        <!-- Calendar header (days of week) -->
        <div class="grid grid-cols-6 gap-2 mb-2">
          <div class="h-10"></div> <!-- Empty corner cell -->
          <div 
            v-for="day in daysOfWeek" 
            :key="day.value" 
            class="h-10 bg-blue-50 rounded p-2 flex items-center justify-center font-medium"
            :class="{'bg-blue-100': isToday(day.date)}"
          >
            <div>
              <div class="text-sm">{{ day.label }}</div>
              <div class="text-xs text-gray-500">{{ formatDayDate(day.date) }}</div>
            </div>
          </div>
        </div>
        
        <!-- Time slots -->
        <div class="space-y-1">
          <div 
            v-for="timeSlot in timeSlots" 
            :key="timeSlot.value" 
            class="grid grid-cols-6 gap-2"
          >
            <!-- Time label -->
            <div class="bg-gray-50 rounded p-2 flex items-center justify-center">
              <span class="text-xs font-medium text-gray-600">{{ timeSlot.label }}</span>
            </div>
            
            <!-- Cells for each day -->
            <div 
              v-for="day in daysOfWeek" 
              :key="`${day.value}-${timeSlot.value}`" 
              class="min-h-[80px] bg-gray-50 rounded p-1 relative"
            >
              <!-- Lectures that fit this time slot and day -->
              <div 
                v-for="lecture in getLecturesForDayAndTime(day.value, timeSlot.value)" 
                :key="lecture.id" 
                :class="['p-2 mb-1 rounded text-xs cursor-pointer border-l-4', lecture.color]"
                @click="$emit('edit-lecture', lecture)"
              >
                <div class="font-medium truncate">{{ lecture.title }}</div>
                <div class="text-xs truncate">{{ lecture.venue }}</div>
                <div class="text-xs">{{ lecture.startTime }} - {{ lecture.endTime }}</div>
                <div v-if="lecture.type" class="text-xs">{{ lecture.type }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LectureCalendar',
  props: {
    lectures: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      currentWeekStart: this.getStartOfWeek(new Date()),
      timeSlots: [
        { value: '08:00', label: '08:00' },
        { value: '09:00', label: '09:00' },
        { value: '10:00', label: '10:00' },
        { value: '11:00', label: '11:00' },
        { value: '12:00', label: '12:00' },
        { value: '13:00', label: '13:00' },
        { value: '14:00', label: '14:00' },
        { value: '15:00', label: '15:00' },
        { value: '16:00', label: '16:00' },
        { value: '17:00', label: '17:00' }
      ],
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
    };
  },
  computed: {
    daysOfWeek() {
      return this.days.map((day, index) => {
        const date = new Date(this.currentWeekStart);
        date.setDate(date.getDate() + index);
        
        return {
          value: day,
          label: day.substring(0, 3),
          date: date
        };
      });
    }
  },
  methods: {
    getStartOfWeek(date) {
      const result = new Date(date);
      const day = result.getDay();
      const diff = day === 0 ? 6 : day - 1; // Adjust for Sunday (0) to get Monday as first day
      result.setDate(result.getDate() - diff);
      result.setHours(0, 0, 0, 0);
      return result;
    },
    
    getEndOfWeek(date) {
      const result = new Date(date);
      result.setDate(result.getDate() + 4); // Monday to Friday (5 days)
      return result;
    },
    
    previousWeek() {
      const newDate = new Date(this.currentWeekStart);
      newDate.setDate(newDate.getDate() - 7);
      this.currentWeekStart = newDate;
    },
    
    nextWeek() {
      const newDate = new Date(this.currentWeekStart);
      newDate.setDate(newDate.getDate() + 7);
      this.currentWeekStart = newDate;
    },
    
    goToCurrentWeek() {
      this.currentWeekStart = this.getStartOfWeek(new Date());
    },
    
    formatDateRange(start, end) {
      const options = { month: 'short', day: 'numeric' };
      return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}, ${end.getFullYear()}`;
    },
    
    formatDayDate(date) {
      return date.getDate().toString().padStart(2, '0') + '/' + (date.getMonth() + 1).toString().padStart(2, '0');
    },
    
    isToday(date) {
      const today = new Date();
      return date.getDate() === today.getDate() && 
             date.getMonth() === today.getMonth() && 
             date.getFullYear() === today.getFullYear();
    },
    
    getLecturesForDayAndTime(day, timeSlot) {
      return this.lectures.filter(lecture => {
        if (lecture.day !== day) return false;
        
        // Parse the times to check if the lecture falls within this time slot
        const lectureStartTime = this.parseTime(lecture.startTime);
        const lectureEndTime = this.parseTime(lecture.endTime);
        const slotStartTime = this.parseTime(timeSlot);
        const slotEndTime = slotStartTime + 60; // Add 60 minutes for the next hour
        
        // Check if lecture overlaps with this time slot
        return (
          (lectureStartTime >= slotStartTime && lectureStartTime < slotEndTime) ||
          (lectureEndTime > slotStartTime && lectureEndTime <= slotEndTime) ||
          (lectureStartTime <= slotStartTime && lectureEndTime >= slotEndTime)
        );
      });
    },
    
    parseTime(timeString) {
      if (!timeString) return 0;
      
      const [hours, minutes] = timeString.split(':').map(Number);
      return (hours * 60) + (minutes || 0);
    }
  }
};
</script>

<style scoped>
.lecture-calendar {
  width: 100%;
}
</style>
